import { DocAuditEntry } from '@/types/documents'
import useCreateDetail, {
  UseCreateDetailOptions,
} from '../core-hooks/useCreateDetail'
import useDeleteDetail, {
  UseDeleteDetailOptions,
} from '../core-hooks/useDeleteDetail'
import useDeleteList, {
  UseDeleteListOptions,
} from '../core-hooks/useDeleteList'
import useGetDetail, { UseGetDetailOptions } from '../core-hooks/useGetDetail'
import useGetList, { UseGetListOptions } from '../core-hooks/useGetList'
import useUpdateDetail, {
  UseUpdateDetailOptions,
} from '../core-hooks/useUpdateDetail'
import useUpdateList, {
  UseUpdateListOptions,
} from '../core-hooks/useUpdateList'
import buildService from '../create-service'
import queryKeys from '../query-keys'

const service = buildService<DocAuditEntry>({
  pathCategory: 'doc_audit_entries',
})

export function useGetDocAuditEntry({
  id,
  ...options
}: UseGetDetailOptions<DocAuditEntry>) {
  const query = useGetDetail<DocAuditEntry>({
    id,
    queryKey: queryKeys.docAuditEntries.detail({
      id,
      filters: options.filters,
    }),
    serviceFn: service.getDetail,
    ...options,
  })

  return {
    docAuditEntry: query.data,
    ...query,
  }
}

export function useCreateDocAuditEntry(
  options: UseCreateDetailOptions<DocAuditEntry> = {},
) {
  const mutation = useCreateDetail<DocAuditEntry>({
    serviceFn: service.createDetail,
    ...options,
  })

  return {
    createDocAuditEntry: mutation.mutate,
    createDocAuditEntryAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useUpdateDocAuditEntry(
  options: UseUpdateDetailOptions<DocAuditEntry> = {},
) {
  const mutation = useUpdateDetail<DocAuditEntry>({
    serviceFn: service.updateDetail,
    ...options,
  })

  return {
    updateDocAuditEntry: mutation.mutate,
    updateDocAuditEntryAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useDeleteDocAuditEntry(options: UseDeleteDetailOptions = {}) {
  const mutation = useDeleteDetail<DocAuditEntry>({
    serviceFn: service.deleteDetail,
    ...options,
  })

  return {
    deleteDocAuditEntry: mutation.mutate,
    deleteDocAuditEntryAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useGetDocAuditEntries({
  filters,
  ...options
}: UseGetListOptions<DocAuditEntry> = {}) {
  const query = useGetList<DocAuditEntry>({
    filters,
    queryKey: queryKeys.docAuditEntries.list({ filters }),
    serviceFn: service.getList,
    ...options,
  })

  return {
    docAuditEntries: query.allData,
    ...query,
  }
}

export function useUpdateDocAuditEntries(
  options: UseUpdateListOptions<DocAuditEntry> = {},
) {
  const mutation = useUpdateList<DocAuditEntry>({
    serviceFn: service.updateList,
    ...options,
  })

  return {
    updateDocAuditEntries: mutation.mutate,
    updateDocAuditEntriesAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useDeleteDocAuditEntries({
  sideEffectQueryKeys,
  listQueryKey,
  ...options
}: UseDeleteListOptions = {}) {
  const mutation = useDeleteList<DocAuditEntry>({
    serviceFn: service.deleteList,
    sideEffectQueryKeys,
    listQueryKey,
    ...options,
  })

  return {
    deleteDocAuditEntries: mutation.mutate,
    deleteDocAuditEntriesAsync: mutation.mutateAsync,
    ...mutation,
  }
}
